import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ActionModal } from "./Modal";
import { useStore } from "../../Store";
import {
  Box,
  Container,
  Flex,
  Skeleton,
  Text,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { useMessageParentOrRedirect } from "../../utils/useMessageParentOrRedirect";
import CdrReadySidebar from "../Sidebars/CdrReadySidebar";
import { InfoOutlineIcon } from "@chakra-ui/icons";

const containerPx = {
  base: 5,
  md: 10,
};

export default function PageLayout({
  children,
  backgroundColor,
}: {
  children: React.ReactNode;
  backgroundColor?: string;
}) {
  return (
    <Box
      id="divPageLayout"
      style={{ backgroundColor: backgroundColor }}
      px={5}
      py={3}
    >
      {children}
    </Box>
  );
}

interface PageLayoutWithGetDataProps {
  loading?: boolean;
  hasContainerPadding?: boolean;
  backgroundColor?: string;
  isMockBankScreen?: boolean;
  children?: React.ReactNode;
}

export function PageLayoutWithGetData({
  loading: loadingProp,
  backgroundColor = "none",
  hasContainerPadding = true,
  isMockBankScreen = false,
  children,
}: PageLayoutWithGetDataProps) {
  let navigate = useNavigate();

  const {
    setAccess,
    setToken,
    setSessionDetails,
    setLoading,
    setShowPopup,
    setShowPopupError,
    setPopupErrorMessage,
    setSelectedDataHolder,
    setSelectedConsent,
    setData,
    showPopupError,
    popupErrorMessage,
    loading,
    data,
    sessionDetails,
    isEmbedded,
    sidebarDisplay,
  } = useStore();

  // These breakpoint values are to give the site a better feel and consistency on mobile viewports
  // 62em is the same size as the Chakra "md" sizing (~992px)
  const [isMobileViewport] = useMediaQuery("(min-width: 62em)");

  const shouldDisplaySideBar =
    Boolean(sidebarDisplay) && isMobileViewport && !isMockBankScreen;

  const { messageParentOrRedirect } = useMessageParentOrRedirect();

  useEffect(() => {
    setLoading(true);
  }, []);

  function ClearData() {
    let flow = sessionDetails?.flow;
    setLoading(false);
    setPopupErrorMessage("");
    setSelectedConsent(null);
    setSelectedDataHolder(null);

    setShowPopup(false);
    setShowPopupError(false);

    if (data && (flow === "consent" || flow === "direct-to-bank" || flow === "amend")) {
      const errorData = {
        state: data?.session.state!,
        consentId: data?.consent.id!,
        flow: sessionDetails?.flow!,
        isEmbedded: isEmbedded,
        message: "",
        redirectUriBase: data?.session.redirectUri,
        sessionId: sessionDetails?.sessionId,
      };

      setAccess(false);
      setToken(null);
      setData(null);
      setSessionDetails(null);
      sessionStorage.clear();
      localStorage.clear();

      messageParentOrRedirect("ERROR", errorData);
    } else if (flow === "manage") {
      navigate("/DashboardLanding");
    } else navigate("/");
  }

  let isLoading = loadingProp ?? loading;

  return (
    <Box
      backgroundColor={"#f2f2f2"}
      h={isEmbedded ? "100vh" : ""}
      minH={"100vh"}
      w="100%"
    >
      <VStack spacing={1}>
        {/*----------------- @DEBT: 2024-09-11, this can be ripped out when we move CdrReady out of beta ----------------------------------------*/}
        {/*----------------- Also setting the height of the below Flex back to 95vh -------------------------------------------------------------*/}
        {shouldDisplaySideBar && (
          <Box
            w="full"
            bg="#6661c2"
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={3}
          >
            <InfoOutlineIcon color="white" />
            <Text ml={2} color="white">
              Beta Release
            </Text>
          </Box>
        )}
        {/*--------------------------------------------------------------------------------------------------------------------------------------*/}
        <Flex mt={3} w={"100%"} h={shouldDisplaySideBar ? "90vh" : ""}>
          {shouldDisplaySideBar && <CdrReadySidebar />}

          <Box overflowX={"hidden"} overflowY={"auto"} flex="6">
            {isLoading ? (
              <>
                <Box py={10} px={containerPx}>
                  <Skeleton height="50px" mb={4} />
                  <Skeleton height="20px" mb={2} mr={10} />
                  <Skeleton height="20px" mr={16} />
                </Box>
              </>
            ) : (
              <Container
                shadow={{ base: "none", md: "lg" }}
                borderRadius="lg"
                bg="white"
                maxW={"container.sm"}
                px={0}
                mb={5}
              >
                <Box
                  id="pageLayoutWithGetData"
                  style={{ backgroundColor: backgroundColor }}
                  px={hasContainerPadding ? containerPx : undefined}
                  py={hasContainerPadding ? 3 : undefined}
                  borderRadius={"lg"}
                >
                  {children}
                  <ActionModal
                    data-testid="modalError"
                    show={showPopupError}
                    title="Error"
                    handleAction={() => ClearData()}
                    confirmButtonText="Ok"
                    handleOnClose={() => {
                      // keep modal open
                    }}
                  >
                    <Text>{popupErrorMessage}</Text>
                  </ActionModal>
                </Box>
              </Container>
            )}
          </Box>
        </Flex>
      </VStack>
    </Box>
  );
}
